import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { DatePipe, formatDate } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { UserAccountApiService } from '../../../core/services/user-account-api.service';
import {
  SIP_ORDER_LINK_ERROR_NO_CONFIRMED_EMAIL,
  SIP_ORDER_LINK_ERROR_UNAVAILABLE,
  UserAccountDetail,
} from '../../../core/services/user-account-api.model';
import { SIP_ORDER_LINK_HISTORY_DAYS, TrackingApiService } from '../../../core/services/tracking-api.service';
import { SIP_ORDER_RESULT_ALREADY_EXISTS } from '../../../core/services/tracking-api.model';
import { EmployeeApiService } from '../../../core/services/employee-api.service';
import { LoggingService } from '../../../core/services/logging.service';
import { SNACKBAR_DURATION, SNACKBAR_ERROR_CONFIG } from '../../../shared/utils/notification-defaults';
import {
  HlmAlertComponent,
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmSpinnerComponent,
  ToastService,
} from '../../../ui';
import {
  SipOrderLinkRow,
  SipOrderLinkStatus,
  sipOrderLinkRowKey,
  sipOrderLinkStatus,
  sipOrderLinkStatusRow,
  toSipOrderLinkRows,
} from './sip-order-link-history.util';

/** One position the SIP order link books, and whether the customer already has it. */
export interface SipOrderItem {
  label: string;
  /** `unknown` when its source could not be read — never shown as missing. */
  state: 'done' | 'missing' | 'unknown';
}

/** Display format for all dates in this section. */
export const SIP_ORDER_LINK_DATE_FORMAT = 'dd.MM.yyyy HH:mm';

type HistoryState = 'loading' | 'loaded' | 'error';

/** Success toast text; without a readable expiry it still confirms the send (a second send would mail again). */
function sentMessage(recipientMasked: string, expiresAt: string): string {
  try {
    return `SIP order link sent to ${recipientMasked} · valid until ${formatDate(expiresAt, SIP_ORDER_LINK_DATE_FORMAT, 'en')}`;
  } catch {
    return `SIP order link sent to ${recipientMasked}`;
  }
}

interface HistoryResult {
  ok: boolean;
  rows: SipOrderLinkRow[];
}

/**
 * "SIP Order Link" block of "Own number on outbound calls" on the Phone Service page: sends the
 * SIP order link (useraccount creates and emails it) and shows its history from the customersupport
 * tracking API. The token and the link
 * never reach AMIE. Sending does not depend on the history: a failed history load leaves the button
 * usable. "Ordered" is only known from tracking — orders placed via the self-service path write no
 * tracking entry and therefore do not hide the button.
 *
 * @ticket PS-6586
 */
@Component({
  selector: 'cmt-sip-order-link-section',
  imports: [DatePipe, HlmAlertComponent, HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmSpinnerComponent],
  templateUrl: './sip-order-link-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SipOrderLinkSectionComponent {
  /** The phone service's user account, loaded by the page; the block stays in edit mode with sending disabled. */
  readonly userAccount = input.required<UserAccountDetail>();
  /** Set by the page when the link has no purpose (number verified or SIP account booked); hides the send button. */
  readonly notNeededReason = input<string | null>(null);
  /** Set by the page when sending cannot help right now (edit mode, plan without the SIP account); disables the button. */
  readonly blockedReason = input<string | null>(null);
  /** The positions the order books, each with its state, shown above the button. */
  readonly books = input<SipOrderItem[]>([]);
  /** Asks the page to open the user account the link is mailed to (where its email is maintained). */
  readonly openUserAccount = output<void>();

  private readonly userAccountApiService = inject(UserAccountApiService);
  private readonly trackingApiService = inject(TrackingApiService);
  private readonly employeeApiService = inject(EmployeeApiService);
  private readonly loggingService = inject(LoggingService);
  private readonly toastService = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly historyDays = SIP_ORDER_LINK_HISTORY_DAYS;
  readonly dateFormat = SIP_ORDER_LINK_DATE_FORMAT;
  readonly alreadyExists = SIP_ORDER_RESULT_ALREADY_EXISTS;
  readonly rowKey = sipOrderLinkRowKey;

  readonly rows = signal<SipOrderLinkRow[]>([]);
  readonly historyState = signal<HistoryState>('loading');
  /** Display names of the employees that sent links, by employee user account id. */
  readonly employeeNames = signal<ReadonlyMap<number, string>>(new Map());
  readonly sending = signal(false);
  private readonly reloadCount = signal(0);

  /** Mirrors the backend gate (blank or unconfirmed email → 409). Text pattern: resetPasswordDisabledReason. */
  readonly disabledReason = computed(() => {
    const blocked = this.blockedReason();
    if (blocked) return blocked;
    const ua = this.userAccount();
    if (!ua.email?.trim()) return 'No email address set';
    if (!ua.emailConfirmed) return 'Email address not confirmed';
    return '';
  });

  readonly status = computed<SipOrderLinkStatus>(() => sipOrderLinkStatus(this.rows(), new Date()));

  /** Key of the history row that carries the status badge (the link the status describes). */
  readonly statusRowKey = computed(() => {
    const row = sipOrderLinkStatusRow(this.rows());
    return row ? sipOrderLinkRowKey(row) : null;
  });

  /** Hidden once the customer ordered or the link is not needed; shown (enabled per email state) while the history loads or failed. */
  readonly showSendButton = computed(() => this.status() !== 'ordered' && !this.notNeededReason());

  readonly sendLabel = computed(() => (this.status() === 'none' ? 'Send SIP order link' : 'Send SIP order link again'));

  readonly statusBadge = computed<{ label: string; variant: 'success' | 'accent' | 'outline' | 'secondary' } | null>(() => {
    switch (this.status()) {
      case 'ordered': return { label: 'Ordered', variant: 'success' };
      case 'opened': return { label: 'Opened, not ordered', variant: 'accent' };
      case 'sent': return { label: 'Sent', variant: 'outline' };
      case 'expired': return { label: 'Expired', variant: 'secondary' };
      default: return null;
    }
  });

  constructor() {
    /**
     * Reloads on account switch and after each send. switchMap drops a late response for an
     * account that is no longer shown (Style Guide §19 #8).
     */
    toObservable(computed(() => ({ id: this.userAccount().id, n: this.reloadCount() })))
      .pipe(
        tap(() => this.historyState.set('loading')),
        switchMap(({ id }) => this.trackingApiService.getSipOrderLinkEntries(id).pipe(
          map((entries): HistoryResult => ({ ok: true, rows: toSipOrderLinkRows(entries) })),
          catchError(() => of<HistoryResult>({ ok: false, rows: [] })),
        )),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        this.rows.set(result.rows);
        this.historyState.set(result.ok ? 'loaded' : 'error');
        this.resolveEmployeeNames(result.rows);
      });
  }

  /** Name of the employee that sent a link; the id when the lookup failed or is still running. */
  employeeLabel(id: number | null): string {
    if (id == null) return 'unknown employee';
    return this.employeeNames().get(id) ?? `employee #${id}`;
  }

  reloadHistory(): void {
    this.reloadCount.update(n => n + 1);
  }

  /**
   * Asks for confirmation, then sends the link. The dialog names the account's current email —
   * the masked form only exists in the answer of the send call and is shown in the success toast.
   */
  send(): void {
    if (this.disabledReason() || this.notNeededReason() || this.sending()) return;
    const ua = this.userAccount();
    const fullName = `${ua.firstName} ${ua.lastName}`.trim() || 'The customer';
    const data: ConfirmDialogData = {
      title: 'Send SIP order link',
      message: 'Send a SIP order link to this address?',
      highlight: ua.email ?? '',
      note: `${fullName} confirms the order in their own browser; you will not see the link.`,
      confirmLabel: 'Send',
      confirmColor: 'primary',
      icon: 'send',
    };

    this.dialog.open(ConfirmDialogComponent, { data }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed || this.sending()) return;
        this.sending.set(true);
        /**
         * Not tied to the component's lifetime: leaving the page or switching the phone service destroys
         * this block, and cancelling the request would hide the outcome of a send the server may
         * already have done — the employee would then send a second email. ToastService is root-scoped.
         */
        this.userAccountApiService.sendSipOrderLink(ua.id).subscribe({
          next: created => {
            this.sending.set(false);
            /** Metadata only — no email, no masked recipient (CLAUDE.md Logging). */
            this.loggingService.usage('sip_order_link.sent', { userAccountId: ua.id, linkId: created.linkId });
            this.reloadHistory();
            /** The longer duration leaves time to read address and date; the send succeeded either way. */
            this.toastService.open(sentMessage(created.recipientMasked, created.expiresAt), 'OK', { duration: SNACKBAR_DURATION.error });
          },
          error: (err: HttpErrorResponse) => {
            this.sending.set(false);
            if (err?.status === 409 && err.error?.error === SIP_ORDER_LINK_ERROR_NO_CONFIRMED_EMAIL) {
              this.toastService.open('No confirmed email address — SIP order link not sent', 'OK', SNACKBAR_ERROR_CONFIG);
              return;
            }
            /** The backend message is a server detail — nothing the employee can act on. */
            if (err?.status === 500 && err.error?.error === SIP_ORDER_LINK_ERROR_UNAVAILABLE) {
              this.toastService.open('SIP order link is temporarily unavailable — please try again later', 'OK', SNACKBAR_ERROR_CONFIG);
              return;
            }
            this.toastService.open(`Failed to send SIP order link (HTTP ${err?.status ?? 'error'})`, 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
      });
  }

  /** One lookup per distinct, not yet known sender; `findEmployeeById` yields null on any error. */
  private resolveEmployeeNames(rows: readonly SipOrderLinkRow[]): void {
    const known = this.employeeNames();
    const ids = [...new Set(rows.map(r => r.issuedBy).filter((id): id is number => id != null && !known.has(id)))];
    if (ids.length === 0) return;
    forkJoin(ids.map(id => this.employeeApiService.findEmployeeById(id)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(employees => {
        const next = new Map(this.employeeNames());
        employees.forEach(employee => {
          const name = employee ? `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() : '';
          if (employee && name) next.set(Number(employee.id), name);
        });
        this.employeeNames.set(next);
      });
  }
}
